<template>
  <div class="setup-step">
    <h3>{{ s__('Setup|Step 1: Language & Timezone') }}</h3>
    <p class="step-description">
      {{
        s__(
          'Setup|Configure your preferred language and timezone. This affects how dates, times, and interface text are displayed.'
        )
      }}
    </p>

    <div class="form-row">
      <label>{{ s__('Setup|Language') }}</label>
      <select v-model="preferredLanguage" :disabled="saving || loadingTimezones" class="select">
        <option value="en">English</option>
        <option value="ru">Русский</option>
      </select>
      <div class="help-text">
        {{
          s__(
            'Setup|Choose your preferred language or let the app detect it from your Telegram settings'
          )
        }}
      </div>
    </div>

    <div class="form-row">
      <label>{{ s__('Setup|Timezone') }}</label>
      <select v-model="preferredTimezone" :disabled="saving || loadingTimezones" class="select">
        <option v-for="tz in timezones" :key="tz.value" :value="tz.value">
          {{ tz.label }}
        </option>
      </select>
      <div class="help-text">
        {{ s__('Setup|Choose your preferred timezone for displaying dates and times') }}
      </div>
    </div>

    <div v-if="saveError" class="error">{{ saveError }}</div>
    <div v-if="saveSuccess" class="success">{{ s__('Setup|Preferences saved successfully!') }}</div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, loadTranslations } from '@/locale'

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
  timezones: {
    type: Array,
    default: () => [],
  },
  detectedTimezone: {
    type: String,
    default: '',
  },
  saving: {
    type: Boolean,
    default: false,
  },
})

const preferredLanguage = ref(null)
const preferredTimezone = ref(null)
const originalPreferredLanguage = ref(null)
const originalPreferredTimezone = ref(null)
const loadingTimezones = ref(false)
const saveError = ref('')
const saveSuccess = ref(false)

const UPDATE_PREFERENCES_MUTATION = gql`
  mutation UpdateUserPreferences(
    $userId: ID!
    $preferredLanguage: String
    $preferredTimezone: String
  ) {
    updateUserPreferences(
      userId: $userId
      preferredLanguage: $preferredLanguage
      preferredTimezone: $preferredTimezone
    ) {
      user {
        id
        preferredLanguage
        preferredTimezone
      }
      errors
    }
  }
`

const { mutate: updatePreferences } = useMutation(UPDATE_PREFERENCES_MUTATION)

// Initialize from user
watch(
  () => [props.user, props.timezones],
  ([userData, timezonesList]) => {
    if (userData) {
      preferredLanguage.value = userData.preferredLanguage || 'en' // Default to 'en' if null

      // Default to Europe/Moscow if timezone is not set
      if (!userData.preferredTimezone && timezonesList && timezonesList.length > 0) {
        const moscowTimezone = timezonesList.find(tz => tz.value === 'Europe/Moscow')
        preferredTimezone.value = moscowTimezone ? 'Europe/Moscow' : timezonesList[0].value
      } else {
        preferredTimezone.value = userData.preferredTimezone
      }

      // Keep the original persisted values to enforce explicit save when required fields are missing.
      originalPreferredLanguage.value = userData.preferredLanguage
      originalPreferredTimezone.value = userData.preferredTimezone
    }
  },
  { immediate: true }
)

// Watch for language changes and update locale immediately
watch(preferredLanguage, newLanguage => {
  if (newLanguage) {
    // Update HTML lang attribute
    document.documentElement.lang = newLanguage
    // Reload translations
    loadTranslations(newLanguage)
  } else {
    // Auto-detect: reset to default
    const detectedLocale = document.documentElement.lang || 'en'
    document.documentElement.lang = detectedLocale
    loadTranslations(detectedLocale)
  }
})

// Check if there are unsaved changes
function hasChanges() {
  return (
    preferredLanguage.value !== originalPreferredLanguage.value ||
    preferredTimezone.value !== originalPreferredTimezone.value
  )
}

// Reset changes
function reset() {
  preferredLanguage.value = originalPreferredLanguage.value
  preferredTimezone.value = originalPreferredTimezone.value
  saveError.value = ''
  saveSuccess.value = false
}

// Expose methods to parent
defineExpose({
  hasChanges,
  isComplete() {
    return !!(preferredLanguage.value && preferredTimezone.value)
  },
  reset,
  async save() {
    if (!props.user?.id) return

    saveError.value = ''
    saveSuccess.value = false

    try {
      const { data } = await updatePreferences({
        userId: props.user.id,
        preferredLanguage: preferredLanguage.value,
        preferredTimezone: preferredTimezone.value,
      })

      if (data?.updateUserPreferences?.errors?.length > 0) {
        throw new Error(data.updateUserPreferences.errors.join(', '))
      }

      saveSuccess.value = true
      originalPreferredLanguage.value = preferredLanguage.value
      originalPreferredTimezone.value = preferredTimezone.value
      // Return saved data instead of emitting event - parent will handle step advancement
      return {
        preferredLanguage: preferredLanguage.value,
        preferredTimezone: preferredTimezone.value,
      }
    } catch (e) {
      saveError.value = e.message || 'Failed to save preferences'
      return false
    }
  },
})
</script>

<style scoped>
.setup-step {
  max-width: 600px;
}

.setup-step h3 {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.form-row {
  margin-bottom: 16px;
}

.form-row label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
}

.select {
  width: 100%;
  max-width: 400px;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.select:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.help-text {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

.error {
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
}

.success {
  color: #2e7d32;
  font-size: 14px;
  margin-top: 8px;
}
</style>
